/**
 * Adapter: drive the outbox off a `@norkron/web-offline-store` table.
 *
 * The sibling lib is an *optional* peer dependency — we do not import it at
 * module scope. Instead the consumer passes in the already-constructed store
 * handle (the minimal slice we need), keeping `web-sync-queue` decoupled from
 * the sibling's exact surface while it is still pre-1.0. This also means the
 * sibling can evolve its API without breaking us as long as the four verbs
 * below are satisfiable.
 *
 * Expected shape of an offline-store collection (duck-typed):
 *
 * ```ts
 * interface OfflineCollection {
 *   put(row): Promise<{ id: number }>            // returns assigned key
 *   query(pred): Promise<Row[]>                  // filtered rows
 *   patch(id, partial): Promise<void>
 *   all(): Promise<Row[]>
 *   delete(id): Promise<void>                    // remove a row by key
 * }
 * ```
 *
 * `@norkron/web-offline-store` satisfies `delete` via `store.delete(table, key)`
 * (and the per-collection scope's `delete`); any wrapper that can remove a row
 * by primary key is a valid backing collection.
 *
 * Rows written by the offline store carry `__version` / `__updatedAt`; the
 * adapter preserves them so the conflict policies (esp. `lww`) can read them.
 */

import type { OutboxEntry, OutboxStore, ProgressSnapshot } from './types'

export interface OfflineCollectionLike {
	put(row: Record<string, unknown>): Promise<{ id: number }>
	query(
		predicate: (row: Record<string, unknown>) => boolean,
	): Promise<Array<Record<string, unknown>>>
	patch(id: number, partial: Record<string, unknown>): Promise<void>
	all(): Promise<Array<Record<string, unknown>>>
	/** Delete a row by its primary key. No-op when the key is absent. */
	delete(id: number): Promise<void>
}

export class OfflineStoreOutboxAdapter implements OutboxStore {
	constructor(private readonly collection: OfflineCollectionLike) {}

	async add(entry: Omit<OutboxEntry, 'id'>): Promise<number> {
		const res = await this.collection.put({ ...entry })
		return res.id
	}

	async listPending(limit: number): Promise<OutboxEntry[]> {
		const rows = await this.collection.query((r) => r.status === 'pending')
		return rows
			.map((r) => r as unknown as OutboxEntry)
			.sort((a, b) => a.id - b.id)
			.slice(0, Math.max(0, limit))
	}

	async listInFlight(): Promise<OutboxEntry[]> {
		const rows = await this.collection.query((r) => r.status === 'in_flight')
		return rows
			.map((r) => r as unknown as OutboxEntry)
			.sort((a, b) => a.id - b.id)
	}

	async update(id: number, patch: Partial<OutboxEntry>): Promise<void> {
		await this.collection.patch(id, patch as Record<string, unknown>)
	}

	async counts(): Promise<ProgressSnapshot> {
		const rows = await this.collection.all()
		const snap: ProgressSnapshot = {
			pending: 0,
			in_flight: 0,
			succeeded: 0,
			failed: 0,
		}
		for (const r of rows) {
			const s = r.status as OutboxEntry['status']
			if (s === 'pending') snap.pending++
			else if (s === 'in_flight') snap.in_flight++
			else if (s === 'succeeded') snap.succeeded++
			else if (s === 'failed') snap.failed++
		}
		return snap
	}

	async pruneSucceeded(): Promise<number> {
		const succeeded = await this.collection.query((r) => r.status === 'succeeded')
		for (const r of succeeded) {
			await this.collection.delete(r.id as number)
		}
		return succeeded.length
	}
}
